import { Maybe, PostAccessLevel, PostPublishStatus, PostStage } from '@/graphql/generated/types.generated';
import { postIsLocked } from './post-is-locked.util';

export type PostAccessPolicyPost = {
    accessLevel?: Maybe<PostAccessLevel>;
    stage: PostStage;
    publishAt?: Maybe<string>;
    profiles?: Maybe<{ publishStatus: PostPublishStatus }[]>;
    reviewers?: Maybe<unknown[]>;
};

export type PostActionPolicy = {
    approve: boolean;
    /** Takes the post back from the client, which is what makes it editable again. */
    cancelClientReview: boolean;
    delete: boolean;
    duplicate: boolean;
    edit: boolean;
    move: boolean;
    /**
     * Which of the two review destinations leads when both are offered. A team that configured
     * reviewers is telling us internal review is its normal path; a team that configured none has
     * only the client, and giving internal review the prominent slot there would point at a button
     * that is not even rendered.
     */
    primaryReview: 'team' | 'client';
    publishNow: boolean;
    reject: boolean;
    schedule: boolean;
    sendForReview: boolean;
    sendToClientReview: boolean;
    unschedule: boolean;
};

/**
 * The stages a post can be handed to the client from. Mirrors CLIENT_REVIEW_SOURCE_STAGES on the
 * server, which is the list that actually decides.
 *
 * Internal approval is deliberately not a prerequisite. Requiring it left the client flow
 * unreachable for a workspace with no post reviewers configured: TEAM_APPROVED is only reachable
 * through a team review such a workspace can never start.
 *
 * ClientApproved is included because editing an approved post is allowed and silently invalidates
 * the approval the client gave, so the team has to be able to ask again.
 *
 * TEAM_REVIEW is absent on purpose. The reviewers hold the post there and have been notified about
 * it, so it has to be approved or rejected before it can go anywhere else.
 */
const CLIENT_REVIEW_SOURCE_STAGES: PostStage[] = [
    PostStage.Idea,
    PostStage.Draft,
    PostStage.TeamRevision,
    PostStage.TeamApproved,
    PostStage.ClientRevision,
    PostStage.ClientApproved,
];

/** In review by someone — the team or the client. Used to suppress edits and re-submission. */
export const isReviewStage = (stage: PostStage): boolean => {
    return [PostStage.TeamReview, PostStage.ClientReview].includes(stage);
};

export const hasFullPostAccess = (post: Pick<PostAccessPolicyPost, 'accessLevel'>): boolean => {
    return post.accessLevel === PostAccessLevel.Full;
};

export const getPostActionPolicy = (post: PostAccessPolicyPost): PostActionPolicy => {
    const hasFullAccess = hasFullPostAccess(post);
    const locked = postIsLocked(post);
    const reviewStage = isReviewStage(post.stage);
    const hasReviewers = (post.reviewers || []).length > 0;

    return {
        // TEAM review only. `approvePost` / `rejectPost` move a post to TEAM_APPROVED / TEAM_REVISION
        // and the server requires TEAM_REVIEW, so offering them on a CLIENT_REVIEW post would render
        // buttons that always fail. The client's own decision is a separate pair of mutations, taken
        // by the client on the shared view rather than by the team here.
        approve: post.stage === PostStage.TeamReview,
        reject: post.stage === PostStage.TeamReview,
        // Handing the post to the client, from anywhere the work is still the team's. Mirrors
        // validateSendToClientReview on the server, so the button never appears where the mutation
        // would be rejected.
        sendToClientReview: hasFullAccess && !locked && CLIENT_REVIEW_SOURCE_STAGES.includes(post.stage),
        // The way out of client review, and the only action offered there. Same gate as sending,
        // since it is that decision reversed.
        cancelClientReview: hasFullAccess && !locked && post.stage === PostStage.ClientReview,
        primaryReview: hasReviewers ? 'team' : 'client',
        unschedule: post.stage === PostStage.Scheduled && !locked,

        duplicate: hasFullAccess,
        // ClientReview belongs here because the server refuses updates in that stage, so work
        // cannot change underneath a client who is reading it. Without it the app rendered an edit
        // button that always failed, with the server's untranslated "Invalid post stage" as the
        // only explanation. Withdrawing the post is what unlocks editing again.
        edit:
            hasFullAccess &&
            ![PostStage.Scheduled, PostStage.ClientReview, PostStage.Published].includes(post.stage),
        delete:
            hasFullAccess &&
            !(post.stage === PostStage.Published || (post.stage === PostStage.Scheduled && locked)),
        move:
            hasFullAccess &&
            !(
                post.stage === PostStage.ClientReview ||
                post.stage === PostStage.Published ||
                (post.stage === PostStage.Scheduled && locked)
            ),
        schedule:
            hasFullAccess &&
            !locked &&
            ![
                PostStage.Scheduled,
                PostStage.TeamReview,
                PostStage.ClientReview,
                PostStage.Published,
            ].includes(post.stage),
        publishNow: hasFullAccess && !locked && post.stage !== PostStage.Published,
        sendForReview:
            hasFullAccess &&
            hasReviewers &&
            !reviewStage &&
            ![PostStage.Scheduled, PostStage.Published].includes(post.stage) &&
            !locked,
    };
};

export const hasPostMenuActions = (policy: PostActionPolicy): boolean => {
    return (
        policy.approve ||
        policy.cancelClientReview ||
        policy.duplicate ||
        policy.reject ||
        policy.schedule ||
        policy.unschedule ||
        policy.publishNow ||
        policy.sendForReview ||
        policy.sendToClientReview ||
        policy.delete
    );
};
